import React from 'react';
import { Button, Avatar } from '@framework-controls/core';
import css from './CalendarSelectBtn.module.scss';
import classNames from 'classnames';
import { strings } from 'src/localization';
import NoProfile from 'src/content/icons/Profile2.svg';
import NoProfileWhite from 'src/content/icons/ProfileWhite.svg';
import { IconProfile2, ProfileAudiImg as NoProfileAudi } from 'src/content/icons';
import { getAdvisorImage, getCalendarSelectBtnAvatarColor } from 'src/helpers/AvatarHelper/AvatarHelper';
import { THEME } from 'src/constants/ThemeNames';
import { CalendarSelectButtonIds } from 'src/enums';

interface IProps {
    id: CalendarSelectButtonIds;
    label: string;
    icon: React.ReactElement;
    selected: boolean;
    theme: string;
    colorSensitiveEnabled: boolean;
    advisorURL: string;
    advisorName: string;
    onClick: (id: CalendarSelectButtonIds, e: any) => void;
}

/** Single sub-tab button for the CalendarSelect view (Date Select / Time Filter / Advisor Select).
 *  Renders the button with theme-specific styling; when used as the Advisor Select tab it
 *  displays the advisor's avatar image instead of the icon prop. */
const CalendarSelectBtn = (props: IProps) => {
    const btnClassName = classNames(
        css.calBtn,
        {
            [css.calBtnChecked]: props.selected,
            [css.calBtnCS]: props.theme === 'colorSensitive' ||
                (props.colorSensitiveEnabled && props.theme !== 'buick' && props.theme !== 'bmw' &&
                    props.theme !== 'infiniti' && props.theme !== 'honda'),
            [css.calBtnCA]: props.theme === 'cadillac',
            [css.calBtnCV]: props.theme === 'chevrolet' || props.theme === 'mercedesBenz',
            [css.calBtnHonda]: props.theme === 'honda',
            [css.calBtnHyundai]: props.theme === 'hyundai',
            [css.calBtnBuick]: props.theme === 'buick',
            [css.calBtnBMW]: props.theme === 'bmw',
            [css.calBtnFord]: props.theme === 'ford',
            [css.calBtnIN]: props.theme === 'infiniti',
            [css.calBtnMazda]: props.theme === 'mazda',
            [css.calBtnAcura]: props.theme === 'acura',
            [css.calBtnGenesis]: props.theme === 'genesis',
            [css.calBtnLexus]: props.theme === 'lexus',
            [css.calBtnAudi]: props.theme === THEME.AUDI,
        },
    );

    const onClick = (e: any) => {
        props.onClick(props.id, e);
    };

    /* checks to see if we are on the advisor tab. If so we take the advisor image
    URL and use it to make an Avatar component for the tab */
    let avatar = <Avatar/>;
    if (props.id === CalendarSelectButtonIds.AdvSelect) {
        let image = getAdvisorImage(props.advisorURL, NoProfile);

        const AdvImgAltTag = props.advisorName + ' ' + strings.Photo;
        const avatarColor = getCalendarSelectBtnAvatarColor(props.theme, props.selected, !!props.advisorURL);
        if (props.theme === THEME.COLOR_SENSITIVE) {
            image = NoProfileWhite;
        }
        if (!props.advisorURL && props.selected && props.theme === THEME.TOYOTA_SPM) {
            image = NoProfileWhite;
        }
        if (props.selected && props.theme === THEME.CADILLAC) {
            image = NoProfileWhite;
        }
        // The avatar is filled $themeButtonSecondaryBackground for Audi (see the stylesheet),
        // so the placeholder silhouette has to be light to read against it. Its own copy rather
        // than NoProfileWhite: the colour is baked into the file as a fill, and this is passed
        // as a src URL, so CSS cannot retint it - ProfileAudi.svg is the same path at Alpha's
        // #FCFCFD. Only when there is no advisor photo; a real photo is used as-is.
        if (!props.advisorURL && props.theme === THEME.AUDI) {
            image = NoProfileAudi;
        }

        avatar = <Avatar
            elevation={0}
            color={avatarColor}
            borderThickness={0}
            src={image}
            alt={AdvImgAltTag}
        />;

        if (props.theme === 'hyundai' && (image === NoProfile || image === NoProfileWhite)) {
            const avatarHyundaiClass = props.selected ? css.avatarSelectedHyundai : css.avatarHyundai;
            avatar = <IconProfile2 className={avatarHyundaiClass} />;
        }
    }

    return (
        <div className={btnClassName}>
            <Button
                id={props.id + 'Btn'}
                onClick={onClick}
                variant='calBtn'
                disableRipple
                type={'text'}>
                <div className={css.btnArea}>
                    <div className={css.contentsIconDiv}>
                        <div className={css.calBtnContents}>
                            <div className={css.avatar}>
                                {props.id === CalendarSelectButtonIds.AdvSelect ? avatar : props.icon}
                            </div>
                        </div>
                    </div>
                    <div className={css.contentsTitleDiv}>
                        <h3 className={css.calBtnContents}>{props.label}</h3>
                    </div>
                </div>
            </Button>
        </div>
    );
};

export default CalendarSelectBtn;